import "./toggle-switch.css";

// ToggleSwitch — a small binary on/off control. Same family as the rest
// of the design system (recessed track, raised puck, ice-blue glow when
// on), distinct from a SynthButton's toggle variant which reads as
// "press to fire" — the switch reads as "state, pick one".
//
// Used for binary parameters that don't have a hardware-button equivalent:
// MIDI thru, tempo sync, auto-gain, voice mono/poly, etc.

export type ToggleForcedState = "off" | "on" | "hover" | "disabled";

export interface ToggleSwitchOptions {
  container: HTMLElement;
  /** Optional text label. Lowercase, letter-spaced; lights ice-blue when on. */
  label?: string;
  /** Which side of the track the label sits on. Default "right". */
  labelPosition?: "left" | "right";
  /** Initial state. Default false (off). */
  active?: boolean;
  onToggle?: (active: boolean) => void;
  /** Demo-only: pin a visual state and disable real interaction. */
  forceState?: ToggleForcedState;
}

export class ToggleSwitch {
  private root: HTMLDivElement;
  private track: HTMLButtonElement;
  private labelEl: HTMLSpanElement | null = null;
  private active: boolean;
  private onToggle: ((active: boolean) => void) | undefined;
  private forced: ToggleForcedState | undefined;

  constructor(opts: ToggleSwitchOptions) {
    this.active = opts.active ?? false;
    this.forced = opts.forceState;
    if (opts.onToggle) this.onToggle = opts.onToggle;

    this.root = document.createElement("div");
    this.root.className = "toggle-switch";
    this.applyClasses();

    this.track = document.createElement("button");
    this.track.type = "button";
    this.track.className = "toggle-switch-track";
    this.track.setAttribute("role", "switch");
    this.track.setAttribute("aria-checked", this.active ? "true" : "false");
    if (this.forced === "disabled") this.track.disabled = true;
    if (opts.label) this.track.setAttribute("aria-label", opts.label);

    const fill = document.createElement("div");
    fill.className = "toggle-switch-fill";
    const puck = document.createElement("div");
    puck.className = "toggle-switch-puck";
    this.track.append(fill, puck);

    if (opts.label) {
      this.labelEl = document.createElement("span");
      this.labelEl.className = "toggle-switch-label";
      this.labelEl.textContent = opts.label;
    }

    const labelPos = opts.labelPosition ?? "right";
    if (this.labelEl && labelPos === "left") {
      this.root.append(this.labelEl, this.track);
    } else {
      this.root.append(this.track);
      if (this.labelEl) this.root.append(this.labelEl);
    }

    if (!this.forced) {
      this.track.addEventListener("click", (ev) => {
        ev.preventDefault();
        // setActive fires `onToggle` on change, so the click handler
        // doesn't fire it again — state-changes-trigger-callback is a
        // single owner.
        this.setActive(!this.active);
      });
    }

    opts.container.append(this.root);
  }

  isActive(): boolean {
    return this.active;
  }

  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    this.root.classList.toggle("is-on", active);
    this.track.setAttribute("aria-checked", active ? "true" : "false");
    // Fire onToggle on every actual state change — programmatic
    // updates (preset load, etc.) should propagate to whatever owns
    // the underlying state the same way a user click does.
    this.onToggle?.(active);
  }

  destroy(): void {
    this.root.remove();
  }

  private applyClasses(): void {
    this.root.classList.toggle("is-on", this.active);
    if (this.forced === "on") this.root.classList.add("is-on");
    if (this.forced === "hover") this.root.classList.add("is-forced-hover");
    if (this.forced === "disabled") this.root.classList.add("is-disabled");
  }
}
